import {
  takeEvery,
  put,
  call,
  all,
  fork,
  takeLatest,
} from 'redux-saga/effects';
import { get, isEmpty } from 'lodash';
import { Auth } from 'aws-amplify';

import history from 'common/utils/history';
import { signUp } from 'services';
import { errorMessageHandler } from 'common/utils/helpers';
import {
  ISagaAction,
  ISetPassword,
  ISignInModal,
  ISignUpModal,
  IVerifyUser,
  IUsername,
  IForgotPassword,
  ISignInVerifyCode,
} from 'common/types';
import { emptyInvestorState } from 'redux-modules/investor/Actions';
import { emptyDealsState } from 'redux-modules/deals/Actions';
import { VC_STATIC_WEBSITE } from 'common/utils/constants';

import {
  USER_LOGIN_LOADING,
  USER_LOGIN,
  USER_LOGIN_COMPLETE,
  ADMIN_SET_PASSWORD,
  SIGN_UP,
  SIGN_UP_SUCCESS,
  SIGN_UP_FAILURE,
  VERIFY_USER,
  VERIFY_SUCCESS,
  ERROR_VERIFY,
  ERROR_LOGIN,
  FORGOT_PASSWORD,
  SUCCESS_FORGOT_PASSWORD,
  ERROR_FORGOT_PASSWORD,
  USER_SET_PASSWORD_FAILURE,
  USER_SET_PASSWORD,
  USER_SET_PASSWORD_SUCCESS,
  ADMIN_SET_PASSWORD_SUCCESS,
  ADMIN_SET_PASSWORD_FAILURE,
  ERROR_SIGN_OUT,
  SIGN_OUT,
  logoutComplete,
  USER_LOGIN_VERIFICATION_PROCESS,
  USER_LOGIN_VERIFICATION,
} from './Actions';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const CryptoJS = require('crypto-js');
type typesObj = {
  user: any;
  attributes: any;
  route?: string;
  challengeName: string;
};

// type responseType = {
//   response: any;
// };

const signUpAPI = async (payload: ISignUpModal) =>
  signUp(payload)
    .then((res) => res)
    .catch((err) => Promise.reject(err));

const verifyUser = async (payload: IVerifyUser) =>
  Auth.confirmSignUp(payload.username, payload.code)
    .then((res) => res)
    .catch((err) => Promise.reject(err));

const signOut = async () =>
  Auth.signOut()
    .then((res) => res)
    .catch((err) => Promise.reject(err));

const forgotPassword = async (payload: IUsername) =>
  Auth.forgotPassword(payload.username)
    .then((data) => data)
    .catch((err) => Promise.reject(err));

const signIn = async (payload: ISignInModal) => {
  const { username, password } = payload;
  return Auth.signIn(username, password)
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const signInVerifyCode = async (payload: ISignInVerifyCode) => {
  const { user, code } = payload;
  return Auth.sendCustomChallengeAnswer(user, code)
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const confirmPassword = async (payload: IForgotPassword) => {
  const { username, password, code } = payload;
  return Auth.forgotPasswordSubmit(username, code, password)
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const newPasswordRequired = async (payload: ISetPassword) => {
  const { newPassword, username, password } = payload;
  return Auth.signIn(username, password)
    .then((res) =>
      Auth.completeNewPassword(res, newPassword).catch((err) =>
        Promise.reject(err),
      ),
    )
    .catch((err) => Promise.reject(err));
};

function* loginUser(data: ISagaAction<ISignInModal>) {
  const { payload } = data;
  try {
    yield put({ type: USER_LOGIN_LOADING });
    const response: typesObj = yield call<any>(signIn, payload);
    const { challengeName } = response;
    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      yield put({ type: ERROR_LOGIN });
      history.push(`/reset-password/${payload.username}`);
    } else if (challengeName === 'CUSTOM_CHALLENGE') {
      yield put({ type: USER_LOGIN_VERIFICATION, payload: response });
    } else {
      const userObj = {
        ...response.attributes,
        role: response.attributes['custom:role'],
        investorId: response.attributes['custom:investor_id'],
        referred: response.attributes['custom:referred'],
      };
      yield put({
        type: USER_LOGIN_COMPLETE,
        payload: { ...userObj },
      });
      history.push('/dashboard');
    }
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: ERROR_LOGIN, payload: message });
  }
}

function* loginVerifyCodeProcess(data: ISagaAction<ISignInVerifyCode>) {
  const { payload } = data;
  try {
    yield put({ type: USER_LOGIN_LOADING });
    const response: typesObj = yield call<any>(signInVerifyCode, payload);
    if (!isEmpty(response.attributes)) {
      // const userObj = {
      //   ...response.attributes,
      //   role: response.attributes['custom:role'],
      //   investorId: response.attributes['custom:investor_id'],
      // };

      Auth.currentAuthenticatedUser().then((res) => {
        const idToken = get(res, 'signInUserSession.idToken.jwtToken');
        const accessToken = get(res, 'signInUserSession.accessToken.jwtToken');
        const refreshToken = get(res, 'signInUserSession.refreshToken.token');

        const secretKey = process.env.REACT_APP_SECRET_KEY;
        const ciphertext = CryptoJS.AES.encrypt(
          JSON.stringify({
            idToken,
            accessToken,
            refreshToken,
          }),
          secretKey,
        ).toString();
        const env = process.env.REACT_APP_ENV;
        const domain = VC_STATIC_WEBSITE[env || 'dev'];
        localStorage.clear();
        window.location.href = `${domain}investor/redirect-auth?authentication=${ciphertext}`;
      });

      // yield put({
      //   type: USER_LOGIN_COMPLETE,
      //   payload: { ...userObj },
      // });
      // history.push('/dashboard');
    } else {
      yield put({ type: ERROR_LOGIN, payload: 'Invalid code!' });
    }
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: ERROR_LOGIN, payload: message });
  }
}

function* userSignOut() {
  try {
    yield call(signOut);
    localStorage.clear();
    yield put(logoutComplete());
    yield put(emptyInvestorState());
    yield put(emptyDealsState());
    history.push('/sign-in');
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: ERROR_SIGN_OUT, payload: message });
  }
}

function* handleForgotPassword(data: ISagaAction<IUsername>) {
  const { payload } = data;
  try {
    yield call<any>(forgotPassword, payload);
    yield put({ type: SUCCESS_FORGOT_PASSWORD, payload: payload.username });
    history.push('/success');
  } catch (err) {
    const code: string = get(err, 'code');
    const message: string =
      code === 'UserNotFoundException'
        ? 'User does not exist'
        : get(err, 'message');
    yield put({ type: ERROR_FORGOT_PASSWORD, payload: message });
  }
}

function* setPassword(data: ISagaAction<ISetPassword>) {
  const { payload } = data;
  try {
    yield call<any>(newPasswordRequired, payload);
    yield put({
      type: ADMIN_SET_PASSWORD_SUCCESS,
      payload: 'Successfully Changed',
    });
    setTimeout(() => {
      history.push('/admin/dashboard');
    }, 3500);
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: ADMIN_SET_PASSWORD_FAILURE, payload: message });
  }
}

function* handleSignUp(data: ISagaAction<ISignUpModal>) {
  const { payload } = data;
  try {
    yield call<any>(signUpAPI, payload);
    yield put({ type: SIGN_UP_SUCCESS, payload: payload.email });
    history.push('/sign-up-success');
  } catch (err) {
    const message: string = errorMessageHandler(err);
    yield put({ type: SIGN_UP_FAILURE, payload: message });
  }
}

function* handleVerifyUser(data: ISagaAction<IVerifyUser>) {
  const { payload } = data;
  try {
    yield call<any>(verifyUser, payload);
    yield put({ type: VERIFY_SUCCESS, payload: 'Successfully Verified' });
    setTimeout(() => history.push('/sign-in'), 3500);
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: ERROR_VERIFY, payload: message });
  }
}

function* handleSubmitPassword(data: ISagaAction<IForgotPassword>) {
  const { payload } = data;
  try {
    yield call<any>(confirmPassword, payload);
    yield put({
      type: USER_SET_PASSWORD_SUCCESS,
      payload: 'Successfully Changed',
    });
    setTimeout(() => history.push('/sign-in'), 3500);
  } catch (err) {
    const message: string = get(err, 'message');
    yield put({ type: USER_SET_PASSWORD_FAILURE, payload: message });
  }
}

function* loginSaga() {
  yield takeEvery(USER_LOGIN, loginUser);
}

function* loginVerifiyCodeSaga() {
  yield takeLatest(USER_LOGIN_VERIFICATION_PROCESS, loginVerifyCodeProcess);
}

function* forgotPasswordSaga() {
  yield takeEvery(FORGOT_PASSWORD, handleForgotPassword);
}

function* setPasswordSaga() {
  yield takeEvery(ADMIN_SET_PASSWORD, setPassword);
}

function* setSignUpSaga() {
  yield takeEvery(SIGN_UP, handleSignUp);
}

function* setUserPassword() {
  yield takeEvery(USER_SET_PASSWORD, handleSubmitPassword);
}

function* verifyUserSaga() {
  yield takeEvery(VERIFY_USER, handleVerifyUser);
}

function* signOutSaga() {
  yield takeLatest(SIGN_OUT, userSignOut);
}

export default function* authSagas() {
  yield all([
    fork(loginSaga),
    fork(loginVerifiyCodeSaga),
    fork(setPasswordSaga),
    fork(setSignUpSaga),
    fork(verifyUserSaga),
    fork(forgotPasswordSaga),
    fork(setUserPassword),
    fork(signOutSaga),
  ]);
}
